import React from "react";
import { Section, SerifH, Eyebrow, PrimaryButton, Bullet, Card } from "../components/ui-kit/Primitives";

const groups = [
  {
    title: "Business & Agency Setup",
    items: [
      "LLC/business formation guidance",
      "EIN and foundational business setup support",
      "Operating agreement guidance",
      "Business email, phone, and professional presence",
      "Brand positioning",
      "Website and online presence planning",
    ],
  },
  {
    title: "Compliance & Credentialing Readiness",
    items: [
      "NCTracks preparation support",
      "NPI guidance",
      "W-9 preparation guidance",
      "Insurance guidance",
      "Policy and procedure development support",
      "Medicaid/provider readiness",
      "Documentation expectations",
      "Internal compliance organization",
    ],
  },
  {
    title: "Operations & Systems",
    items: [
      "Intake workflows",
      "Referral tracking",
      "Client onboarding process",
      "Documentation templates",
      "Staff role structure",
      "Administrative workflows",
      "Scheduling systems",
      "CRM or client management tools",
      "Quality control procedures",
    ],
  },
  {
    title: "Program Development",
    items: [
      "Peer support service model",
      "Community support programming",
      "Recovery-focused services",
      "Life skills programming",
      "Wellness education",
      "Group support concepts",
      "Client journey mapping",
      "Service descriptions",
    ],
  },
  {
    title: "Growth & Funding Readiness",
    items: [
      "Business funding preparation",
      "Bankability structure",
      "Revenue planning",
      "High-ticket service/package strategy",
      "Partnership development",
      "Referral partner outreach",
      "Community organization relationships",
      "Grant and contract readiness",
      "Professional presentation materials",
    ],
  },
  {
    title: "Marketing & Branding",
    items: [
      "Website messaging",
      "Social media positioning",
      "Service descriptions",
      "Brochures/flyers",
      "Calendly setup",
      "Consultation scripts",
      "Lead follow-up process",
      "Community outreach strategy",
    ],
  },
];

const idealClients = [
  "New peer support agencies",
  "Existing peer support agencies that need better structure",
  "Certified Peer Support Specialists starting a business",
  "Behavioral health entrepreneurs",
  "Community-based wellness organizations",
  "Agencies preparing for credentialing or Medicaid readiness",
  "Organizations that need operational systems",
  "Founders who need help packaging and presenting their services",
];

const heroImg = "https://images.unsplash.com/photo-1573164574572-cb89e39749b4?auto=format&fit=crop&w=1400&q=80";

const Consulting = () => (
  <div data-testid="consulting-page">
    <Section className="pt-16">
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-end">
        <div className="lg:col-span-7">
          <Eyebrow>Consulting Services</Eyebrow>
          <SerifH as="h1" className="text-5xl sm:text-6xl">
            Consulting services for peer support agencies & behavioral health startups.
          </SerifH>
        </div>
        <div className="lg:col-span-5 text-slate-600 font-light leading-relaxed">
          <p>
            Starting or growing a peer support agency can feel overwhelming. From business
            structure and insurance to credentialing, documentation, policies, staffing, marketing,
            and funding readiness — there are many moving parts.
          </p>
          <p className="mt-4">
            KBX helps agencies create a stronger foundation so they can operate with confidence,
            serve clients effectively, and position themselves for long-term growth.
          </p>
        </div>
      </div>
      <div className="mt-12 rounded-[2rem] overflow-hidden border border-brand-green/10">
        <img src={heroImg} alt="Consulting" className="w-full h-[360px] sm:h-[460px] object-cover" />
      </div>
    </Section>

    <Section className="bg-brand-mist/50">
      <Eyebrow>What we cover</Eyebrow>
      <SerifH className="text-4xl sm:text-5xl mb-12">Consulting services include</SerifH>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {groups.map((g) => (
          <Card key={g.title} className="p-8">
            <h3 className="font-serif text-2xl text-brand-green">{g.title}</h3>
            <ul className="mt-5 space-y-3">
              {g.items.map((it) => <Bullet key={it}>{it}</Bullet>)}
            </ul>
          </Card>
        ))}
      </div>
    </Section>

    <Section>
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-12">
        <div className="lg:col-span-5">
          <Eyebrow>Who we work with</Eyebrow>
          <SerifH className="text-4xl sm:text-5xl">Ideal clients for this work.</SerifH>
        </div>
        <div className="lg:col-span-7">
          <ul className="grid grid-cols-1 sm:grid-cols-2 gap-y-3 gap-x-8">
            {idealClients.map((c) => <Bullet key={c}>{c}</Bullet>)}
          </ul>
        </div>
      </div>
    </Section>

    <Section className="bg-brand-green text-white">
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 items-center">
        <div className="lg:col-span-8">
          <Eyebrow color="gold">Next step</Eyebrow>
          <SerifH className="text-4xl sm:text-5xl text-white">
            Build your agency with structure, clarity, and confidence.
          </SerifH>
        </div>
        <div className="lg:col-span-4 lg:text-right">
          <PrimaryButton to="/contact" testid="consulting-cta" className="bg-brand-gold text-brand-navy hover:bg-white">
            Book a Consulting Call
          </PrimaryButton>
        </div>
      </div>
    </Section>
  </div>
);

export default Consulting;
